import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Calculator, Search, Loader2, BookOpen, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle2, Info, Copy, X
} from "lucide-react";

const QUICK_METRICS = [
  { key: "cpk", label: "Cpk", placeholder: "e.g., 1.3038" },
  { key: "ppk", label: "Ppk", placeholder: "e.g., 1.67" },
  { key: "p_value", label: "p-value", placeholder: "e.g., 0.847" },
  { key: "anderson_darling", label: "Anderson-Darling", placeholder: "e.g., 0.192" },
  { key: "grubbs", label: "Grubb's Test", placeholder: "e.g., 1.87" },
  { key: "nelson_rule", label: "Nelson Rule", placeholder: "Rule number" },
  { key: "confidence_interval", label: "Confidence Interval", placeholder: "e.g., 95" },
  { key: "power_analysis", label: "Power Analysis", placeholder: "e.g., 0.90" },
  { key: "gauge_rr", label: "Gauge R&R", placeholder: "e.g., 8.5" },
];

const StatExplainerPage = () => {
  const { t } = useTranslation('platform');
  const [metricType, setMetricType] = useState("");
  const [value, setValue] = useState("");
  const [context, setContext] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch(`${API}/auditiq/explain/templates`, { credentials: "include" });
        if (res.ok) { const d = await res.json(); setTemplates(d.templates || []); }
      } catch (e) { console.error(e); }
    };
    fetchTemplates();
  }, []);

  const explain = async (type, val) => {
    const mt = type || metricType;
    const v = val || value;
    if (!mt || !v) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/auditiq/explain`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ metric_type: mt, value: parseFloat(v) || v, context }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setHistory(prev => [{ metric_type: mt, value: v, result: data, timestamp: new Date().toISOString() }, ...prev].slice(0, 10));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <QMSLayout title={t('platform:statExplainer.title')}>
      <div data-testid="stat-explainer-page" className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>Statistical Explainer</h1>
              <p className="text-xs text-[#94A3B8]">Plain-language explanations with mathematical backing and clause citations</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left: Input */}
          <div className="lg:col-span-4 space-y-3">
            {/* Quick metric buttons */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">Select Metric</h3>
              <div className="grid grid-cols-3 gap-1.5">
                {QUICK_METRICS.map(m => (
                  <button key={m.key} onClick={() => { setMetricType(m.key); setResult(null); }}
                    data-testid={`metric-btn-${m.key}`}
                    className={`px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${metricType === m.key ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" : "bg-[#132340] text-[#94A3B8] hover:text-white border border-transparent"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Value input */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider">Enter Value</h3>
              <input data-testid="stat-value-input" value={value} onChange={e => setValue(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") explain(); }}
                placeholder={QUICK_METRICS.find(m => m.key === metricType)?.placeholder || t('statExplainer.enterValue')}
                className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white placeholder-[#94A3B8]/50 focus:border-purple-500 focus:outline-none" />

              {/* Optional context */}
              <div className="space-y-1.5">
                <span className="text-[10px] text-[#94A3B8] uppercase">Optional Context</span>
                <input placeholder="USL" value={context.usl || ""} onChange={e => setContext(p => ({ ...p, usl: e.target.value }))}
                  className="w-full px-2 py-1 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/30" />
                <input placeholder="LSL" value={context.lsl || ""} onChange={e => setContext(p => ({ ...p, lsl: e.target.value }))}
                  className="w-full px-2 py-1 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/30" />
                <input placeholder={t('statExplainer.sampleSize')} value={context.n || ""} onChange={e => setContext(p => ({ ...p, n: e.target.value }))}
                  className="w-full px-2 py-1 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/30" />
              </div>

              <button data-testid="explain-btn" onClick={() => explain()} disabled={loading || !metricType || !value}
                className="w-full py-2 rounded-lg bg-purple-500 text-white font-bold text-sm hover:bg-purple-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Explaining...</> : <><Calculator className="w-4 h-4" /> Explain</>}
              </button>
            </div>

            {/* History */}
            {history.length > 0 && (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-2">
                <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider">Recent Explanations</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {history.map((h, i) => (
                    <button key={i} onClick={() => { setMetricType(h.metric_type); setValue(h.value); setResult(h.result); }}
                      className="w-full text-left p-1.5 rounded hover:bg-[#132340] transition-colors text-xs flex items-center justify-between">
                      <span className="text-white">{h.metric_type} = {h.value}</span>
                      <span className="text-[10px] text-[#94A3B8]">{new Date(h.timestamp).toLocaleTimeString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Explanation */}
          <div className="lg:col-span-8">
            {!result ? (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-12 text-center">
                <Calculator className="w-16 h-16 text-[#1E3A5F] mx-auto mb-4" />
                <h3 className="text-lg text-[#94A3B8] font-medium mb-1">Select a Metric and Enter a Value</h3>
                <p className="text-sm text-[#94A3B8]/60">Get a complete audit-ready explanation: what it means, why it's calculated, the formula, the standard, and what happens if it fails.</p>
              </div>
            ) : (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider">{result.metric}</h3>
                  {result.status && (
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${result.status === "PASS" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>{result.status}</span>
                  )}
                </div>

                {/* Formula */}
                <div className="bg-[#132340] rounded-lg p-3">
                  <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider block mb-1">Formula</span>
                  <p className="text-sm text-[#00C8E0] font-mono">{result.formula}</p>
                  {result.actual_value !== undefined && (
                    <p className="text-xs text-white mt-1">Your Value: <span className="text-[#00C8E0] font-mono font-bold">{String(result.actual_value)}</span></p>
                  )}
                </div>

                {/* What it means */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">What It Means (Plain English)</span>
                    <button onClick={() => copyText(result.what_it_means, "meaning")} className="text-[#94A3B8] hover:text-white">
                      {copiedId === "meaning" ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{result.what_it_means}</p>
                </div>

                {/* Why calculated */}
                <div>
                  <span className="text-[10px] font-bold text-[#1A6BCC] uppercase tracking-wider">Why It's Calculated This Way</span>
                  <p className="text-sm text-[#94A3B8] leading-relaxed mt-1">{result.why_calculated}</p>
                </div>

                {/* Acceptance criterion */}
                <div className="bg-[#132340] rounded-lg p-3 border border-[#1E3A5F]">
                  <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block mb-1">Acceptance Criterion</span>
                  <p className="text-sm text-white">{result.acceptance_criterion}</p>
                </div>

                {/* Standard requires */}
                <div>
                  <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Standard Reference</span>
                  <p className="text-sm text-[#94A3B8] mt-1 flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5 text-purple-400" /> {result.standard_requires}
                  </p>
                </div>

                {/* If it failed */}
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider block mb-1">If It Failed</span>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{result.if_it_failed}</p>
                </div>

                <p className="text-[10px] text-[#94A3B8]/50 text-right">Source: {result.source === "template" ? "Built-in Knowledge Base" : result.source === "ai" ? "AI-Generated" : "Fallback"}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default StatExplainerPage;
